/* <cart-drawer-bogo-carousel> — pages the BOGO upsell's product
 * recommendations 3-at-a-time (see snippets/cart-drawer-bogo.liquid).
 *
 * Loaded globally (from snippets/cart-drawer.liquid's top, outside the part
 * of the drawer that gets replaced via innerHTML on every cart AJAX update)
 * rather than from inside the swapped fragment itself, for the same reason
 * as product-form.js: a <script src> inserted via innerHTML never executes.
 * Custom elements still auto-upgrade correctly wherever they appear once
 * defined, regardless of how they were inserted.
 *
 * No client-side shuffling here (an earlier version did) -- the Liquid
 * template already guarantees no two adjacent items share a model/style by
 * walking the catalog round-robin, and reshuffling the rendered items in
 * JS would undo that guarantee. "Randomized" instead happens server side,
 * by rotating which model leads that round-robin each render.
 */
if (!customElements.get('cart-drawer-bogo-carousel')) {
  customElements.define(
    'cart-drawer-bogo-carousel',
    class CartDrawerBogoCarousel extends HTMLElement {
      connectedCallback() {
        this.pages = Array.from(this.children).filter((el) => el.classList.contains('cart-drawer-bogo__page'));
        if (this.pages.length === 0) return;

        this.currentPage = 0;
        const root = this.closest('.cart-drawer-bogo__upsell') || this.parentElement;
        this.prevButton = root && root.querySelector('.cart-drawer-bogo__nav-btn--prev');
        this.nextButton = root && root.querySelector('.cart-drawer-bogo__nav-btn--next');

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.go(this.currentPage - 1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.go(this.currentPage + 1));

        // Swiping (native scroll-snap) moves `this` directly -- sync the
        // arrows/currentPage to wherever a swipe actually landed, not just
        // to button-driven moves.
        this.addEventListener('scroll', () => this.syncFromScroll(), { passive: true });

        this.updateButtons();
      }

      go(index) {
        const max = this.pages.length - 1;
        this.currentPage = Math.max(0, Math.min(max, index));
        this.slideTo(this.currentPage * this.clientWidth);
        this.updateButtons();
      }

      // Spring-physics page turn (instead of native scroll-behavior: smooth,
      // which is a flat, abrupt jump with no controllable feel) — same
      // attraction/friction integrator as <slider-component>'s arrow-driven
      // slide (assets/global.js#setSlidePosition, the "You may also like" /
      // Bestsellers carousels), so the BOGO upsell's own prev/next arrows
      // glide and settle the same unhurried way instead of snapping over.
      slideTo(position) {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) {
          this.scrollTo({ left: position });
          return;
        }

        if (this.slideAnimationFrame) cancelAnimationFrame(this.slideAnimationFrame);

        // Touch swipes use scroll-snap; mandatory snapping would yank every
        // intermediate scrollLeft below back to a snap point, so pause it
        // while the spring runs (restored once it settles).
        this.style.scrollSnapType = 'none';

        const attraction = 0.025;
        const friction = 0.72;
        let x = this.scrollLeft;
        let velocity = this.slideVelocity || 0;

        const step = () => {
          const delta = position - x;
          velocity += delta * attraction;
          velocity *= friction;
          x += velocity;

          if (Math.abs(delta) < 0.5 && Math.abs(velocity) < 0.05) {
            x = position;
            velocity = 0;
            this.scrollLeft = x;
            this.slideVelocity = 0;
            this.slideAnimationFrame = null;
            this.style.scrollSnapType = '';
            return;
          }

          this.scrollLeft = x;
          this.slideVelocity = velocity;
          this.slideAnimationFrame = requestAnimationFrame(step);
        };

        this.slideAnimationFrame = requestAnimationFrame(step);
      }

      syncFromScroll() {
        if (this.scrollSyncFrame) cancelAnimationFrame(this.scrollSyncFrame);
        this.scrollSyncFrame = requestAnimationFrame(() => {
          const width = this.clientWidth || 1;
          this.currentPage = Math.max(0, Math.min(this.pages.length - 1, Math.round(this.scrollLeft / width)));
          this.updateButtons();
        });
      }

      updateButtons() {
        if (this.prevButton) this.prevButton.hidden = this.currentPage === 0;
        if (this.nextButton) this.nextButton.hidden = this.currentPage === this.pages.length - 1;
      }
    }
  );
}
